import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';

import Loader3D from '../../../components/Loader/Loader3D';
import apiRequest from '../../../api/request';
import DeveloperNote from './DeveloperNote';

/**
 * WHAT IS BEHIND ONE ASSESSMENTS NUMBER.
 *
 * Clicking the Assessments cell on the OGD Point Tracker opens this: every
 * assessment that person wrote in the chosen period, the student name and when it
 * was written, nothing else.
 *
 * It calls the same filter the column itself uses, so the list always adds up to
 * the number on the cell. If the count in the heading ever differs from the number
 * that was clicked, the two have drifted apart and it needs finding, not
 * explaining away.
 *
 * Loaded only when it opens, and thrown away when it closes, so opening the table
 * costs nothing until somebody actually asks.
 */

/** "2026-04-01 00:03:28" shown as "01 Apr 2026, 12:03 am". */
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const prettyDateTime = (raw) => {
  if (!raw) return '';
  // the column is a varchar, so it is read apart by hand rather than trusting the
  // browser to parse whatever shape it happens to be in
  const match = String(raw).match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/,
  );
  if (!match) return String(raw);

  const [, year, month, day, hour24, minute] = match;
  const h = Number(hour24);
  const suffix = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;

  return `${day} ${MONTHS[Number(month) - 1]} ${year}, ${h12}:${minute} ${suffix}`;
};

export default function AssessmentDetailDialog({
  open,
  onClose,
  person, // { userId, oga, team, assessments }
  fromDate,
  toDate,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!open || !person?.userId) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      const res = await apiRequest({
        method: 'get',
        url:
          `ogd-point-tracker/assessments?userId=${person.userId}` +
          `&fromDate=${fromDate}&toDate=${toDate}`,
      });
      if (cancelled) return;

      if (res?.status === 'success') {
        setRows(res.data?.rows || []);
        setMeta(res.data?.meta || null);
      } else {
        setError(res?.message || 'Could not load the assessments');
      }
      setLoading(false);
    })();

    // a fast second click must not let the first answer overwrite the second
    return () => {
      cancelled = true;
    };
  }, [open, person?.userId, fromDate, toDate]);

  // the cell said one number, the list has another. Worth saying out loud rather
  // than leaving somebody to count the rows themselves
  const mismatch =
    !loading &&
    !error &&
    person?.assessments != null &&
    Number(person.assessments) !== rows.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      // the box is nearly the whole window. These tables are wide and are
      // read side by side against the number on the cell, so scrolling a
      // narrow box was the slowest part of checking a figure.
      PaperProps={{ sx: { height: '92vh', maxHeight: '92vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Assessments · {person?.oga}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {rows.length} {rows.length === 1 ? 'assessment' : 'assessments'}
              {meta?.students != null &&
                meta.students !== rows.length &&
                ` · ${meta.students} students`}
              {person?.team ? ` · ${person.team}` : ''}
              {fromDate ? ` · ${fromDate} to ${toDate}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Icon icon="mdi:close" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading && <Loader3D />}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && rows.length === 0 && (
          <Alert severity="info" icon={<Icon icon="mdi:clipboard-text-off-outline" />}>
            No assessments written in this period.
          </Alert>
        )}

        {mismatch && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The table shows {String(person.assessments)} for this period but{' '}
            {rows.length} are listed here. The two should always match.
          </Alert>
        )}

        {!loading && !error && rows.length > 0 && (
          <TableContainer sx={{ maxHeight: 'calc(92vh - 190px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell
                    sx={{ width: 150 }}
                    title="How many assessments this student had in this period, and which one this row is"
                  >
                    No. of assessments
                  </TableCell>
                  <TableCell sx={{ width: 200 }}>Written on</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, index) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{r.name || ''}</TableCell>
                    <TableCell>
                      {r.times > 1 ? (
                        // assessed more than once, so say which one this is out of
                        // how many. Otherwise a repeated name looks like a mistake.
                        <Typography variant="body2" sx={{ color: '#9C27B0', fontWeight: 600 }}>
                          {r.nth} of {r.times}
                        </Typography>
                      ) : (
                        <Typography variant="body2">1</Typography>
                      )}
                    </TableCell>
                    <TableCell>{prettyDateTime(r.doneOn)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <DeveloperNote
          title="Assessments counts REPORTS written in the period, not students seen"
          steps={[
            {
              what: 'Start from the OGA',
              where: 'user.userID  =  ' + (person?.userId ?? ''),
              why: 'the person whose row was clicked on the table',
            },
            {
              what: 'Find the reports they wrote',
              where: 'assessment_report  WHERE assessment_report_created_by = that id',
              why: 'that column is stored as TEXT, so rows that are not a plain number are skipped',
            },
            {
              what: 'Keep the ones written inside the month',
              where: "assessment_report.assessment_report_created_at  BETWEEN '" + fromDate + "' AND '" + toDate + "'",
              why: 'when the report was written, nothing to do with any payment',
            },
            {
              what: 'Get the student name',
              where: 'assessment_report.assessment_report_sro_assignment_id  =  leads.lead_id',
              why: 'that column is MISNAMED. It holds a LEAD id, not an sro_assignment id',
            },
            {
              what: 'Count the reports',
              where: 'COUNT(*)   =   Assessments',
              why: 'the same student assessed twice is two reports and both count',
            },
          ]}
          lines={[
            'KNOWN GAP: this table holds well under half the assessments the department own sheet records. Several OGAs with 100 or more on the sheet have no rows here at all.',
          ]}
        />
      </DialogContent>
    </Dialog>
  );
}
